import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from './utils/Toast';
import Loader from './utils/Loader';
import Navbar from './utils/Navbr'; // Assuming typo 'Navbr' from previous code
import './style/OfferApproval.css';

const OfferApproval = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [offers, setOffers] = useState([]); // Original Data
    const [filteredOffers, setFilteredOffers] = useState([]); // Search Results
    const [searchQuery, setSearchQuery] = useState('');

    // --- AUTH & INIT ---
    const getCookie = (name) => {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    };
    const adminToken = getCookie('adminToken');

    useEffect(() => {
        const init = async () => {
            try {
                // 1. Auth Check
                const authRes = await axios.post('https://publisher.cashbolt.in.net/server/', {
                    action: 'checkAuthAdmin', adminToken
                });

                if (!authRes.data.status) {
                    toast.error('Session expired');
                    // navigate('/login'); // Uncomment in real app
                    return;
                }

                // 2. Fetch Pending Offers
                fetchPendingOffers();

            } catch (err) {
                console.error(err);
                toast.error("Connection Error");
            } finally {
                setLoading(false);
            }
        };

        if(adminToken) init();
        else setLoading(false); 
    }, [navigate, adminToken]);

    const fetchPendingOffers = async () => {
        try {
            const res = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'adminFetchPendingOffers', adminToken
            });
            if (res.data.status) {
                setOffers(res.data.offers);
                setFilteredOffers(res.data.offers);
            }
        } catch (e) { console.error(e); }
    };

    // --- REAL-TIME SEARCH LOGIC ---
    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredOffers(offers);
            return;
        }

        const lowerQ = searchQuery.toLowerCase();
        const filtered = offers.filter(item => 
            (item.offerName && item.offerName.toLowerCase().includes(lowerQ)) ||
            (item.publisherName && item.publisherName.toLowerCase().includes(lowerQ)) ||
            (String(item.offerId).includes(lowerQ)) ||
            (String(item.publisherId).includes(lowerQ))
        );
        setFilteredOffers(filtered);
    }, [searchQuery, offers]);

    // --- ACTIONS ---
    const handleAction = async (id, status) => {
        const actionText = status === 'Approved' ? 'Approve' : 'Reject';
        if(!window.confirm(`Are you sure you want to ${actionText} this offer?`)) return;

        try {
            const res = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'adminUpdateOfferStatus',
                adminToken,
                id, // Unique row ID or Offer ID depending on DB
                status // 'Approved' or 'Rejected'
            });

            if (res.data.status) {
                toast.success(`Offer ${status}`);
                // Remove from local list instantly
                const remaining = offers.filter(o => o.id !== id);
                setOffers(remaining);
                setFilteredOffers(remaining.filter(item => 
                    item.offerName.toLowerCase().includes(searchQuery.toLowerCase())
                ));
            } else {
                toast.error(res.data.message);
            }
        } catch (e) {
            toast.error("Server Error");
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="manage-offers-wrapper_op">
            <Navbar name="Admin" role="Administrator" isAdmin={true} />
            <div className="bg-shape_op shape-1_op"></div>
            <div className="bg-shape_op shape-2_op"></div>

            <div className="content-container_op">
                
                {/* Header & Search */}
                <div className="approval-header_op">
                    <div className="header-text_op">
                        <h2 className="page-title_op">Offer <span className="highlight_op">Approvals</span></h2>
                        <p className="sub-text_op">Review and manage pending publisher offers</p>
                    </div>
                    
                    <div className="search-box_op">
                        <i className="search-icon_op">🔍</i>
                        <input 
                            type="text" 
                            placeholder="Search Offer, Publisher, ID..." 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>

                {/* Table */}
                <div className="table-card_op fade-in_op">
                    <table className="offers-table_op">
                        <thead>
                            <tr>
                                <th>Offer ID</th>
                                <th>Logo</th>
                                <th>Offer Name</th>
                                <th>Pub ID</th>
                                <th>Publisher Name</th>
                                <th className="text-center_op">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredOffers.length > 0 ? filteredOffers.map((offer) => (
                                <tr key={offer.id}>
                                    <td><span className="id-badge_op">#{offer.offerId}</span></td>
                                    <td>
                                        <div className="img-wrapper_op">
                                            <img src={offer.logo || 'https://via.placeholder.com/40'} alt="logo" className="table-logo_op"/>
                                        </div>
                                    </td>
                                    <td className="fw-bold_op">{offer.offerName}</td>
                                    <td>{offer.publisherId}</td>
                                    <td>
                                        <div className="user-info_op">
                                            <span>{offer.publisherName}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="action-btns-row_op">
                                            <button 
                                                className="action-btn_op accept" 
                                                onClick={() => handleAction(offer.id, 'Approved')}
                                                title="Accept"
                                            >
                                                ✓ Accept
                                            </button>
                                            <button 
                                                className="action-btn_op reject" 
                                                onClick={() => handleAction(offer.id, 'Rejected')}
                                                title="Reject"
                                            >
                                                ✕ Reject
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="7" className="no-data_op">
                                        {searchQuery ? 'No matching offers found' : 'No pending offers'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default OfferApproval;